import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchSubjects, updateSubject, deleteSubject,
  fetchModules, createModule, updateModule, deleteModule,
  fetchTopics, createTopic, updateTopic, deleteTopic,
} from "../../pages/services/notesService";
import { fetchCourseById } from "../../pages/services/courseService";
import "../../styles/CourseNotes.css";

const InlineForm = ({ placeholder, onSave, onCancel }) => {
  const [value, setValue] = useState("");
  return (
    <div className="cn-inline-form">
      <input
        className="cn-inline-input"
        autoFocus
        placeholder={placeholder}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") onSave(value);
          if (e.key === "Escape") onCancel();
        }}
      />
      <div className="cn-inline-actions">
        <button className="cn-save-btn" onClick={() => onSave(value)}>Save</button>
        <button className="cn-cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

const InlineEditForm = ({ initial, onSave, onCancel }) => {
  const [value, setValue] = useState(initial);
  return (
    <div className="cn-inline-form" onClick={e => e.stopPropagation()}>
      <input
        className="cn-inline-input"
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") onSave(value);
          if (e.key === "Escape") onCancel();
        }}
      />
      <div className="cn-inline-actions">
        <button className="cn-save-btn" onClick={() => onSave(value)}>Save</button>
        <button className="cn-cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

const CourseNotesPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [topics, setTopics] = useState([]);

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);

  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);

  const [addingModule, setAddingModule] = useState(false);
  const [addingTopic, setAddingTopic] = useState(false);

  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editingTopicId, setEditingTopicId] = useState(null);

  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  useEffect(() => {
    fetchCourseById(courseId).then(setCourse).catch(() => {});
    loadSubjects();
  }, [courseId]);

  const loadSubjects = useCallback(async () => {
    setLoadingSubjects(true);
    try { setSubjects(await fetchSubjects(courseId)); } catch (e) { setSubjects([]); }
    setLoadingSubjects(false);
  }, [courseId]);

  const loadModules = useCallback(async (subjectId) => {
    setLoadingModules(true);
    try { setModules(await fetchModules(courseId, subjectId)); } catch (e) { setModules([]); }
    setLoadingModules(false);
  }, [courseId]);

  const loadTopics = useCallback(async (subjectId, moduleId) => {
    setLoadingTopics(true);
    try { setTopics(await fetchTopics(courseId, subjectId, moduleId)); } catch (e) { setTopics([]); }
    setLoadingTopics(false);
  }, [courseId]);

  const handleSelectSubject = (subj) => {
    if (editingSubjectId === subj._id) return;
    setSelectedSubject(subj);
    setSelectedModule(null);
    setTopics([]);
    setModules([]);
    setAddingModule(false);
    setAddingTopic(false);
    loadModules(subj._id);
  };


  const handleSelectModule = (mod) => {
    if (editingModuleId === mod._id) return;
    setSelectedModule(mod);
    setTopics([]);
    setAddingTopic(false);
    loadTopics(selectedSubject._id, mod._id);
  };

  // Subject CRUD
  const handleUpdateSubject = async (subjectId, title) => {
    if (!title.trim()) return;
    try {
      await updateSubject(courseId, subjectId, title.trim());
      setEditingSubjectId(null);
      if (selectedSubject?._id === subjectId)
        setSelectedSubject(prev => ({ ...prev, title: title.trim() }));
      await loadSubjects();
      showToast("Subject updated");
    } catch { showToast("Error updating subject"); }
  };

  const handleDeleteSubject = async (e, subjectId) => {
    e.stopPropagation();
    if (!window.confirm("Remove this subject from the course? The subject itself will not be deleted.")) return;
    try {
      await deleteSubject(courseId, subjectId);
      if (selectedSubject?._id === subjectId) {
        setSelectedSubject(null);
        setModules([]);
        setTopics([]);
        setSelectedModule(null);
      }
      await loadSubjects();
      showToast("Subject removed from course");
    } catch { showToast("Error removing subject"); }
  };

  // Module CRUD
  const handleAddModule = async (title) => {
    if (!title.trim() || !selectedSubject) return;
    try {
      await createModule(courseId, selectedSubject._id, title.trim());
      setAddingModule(false);
      await loadModules(selectedSubject._id);
      showToast("Module added");
    } catch { showToast("Error adding module"); }
  };

  const handleUpdateModule = async (moduleId, title) => {
    if (!title.trim()) return;
    try {
      await updateModule(courseId, selectedSubject._id, moduleId, title.trim());
      setEditingModuleId(null);
      if (selectedModule?._id === moduleId)
        setSelectedModule(prev => ({ ...prev, title: title.trim() }));
      await loadModules(selectedSubject._id);
      showToast("Module updated");
    } catch { showToast("Error updating module"); }
  };

  const handleDeleteModule = async (e, moduleId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this module? All topics inside will also be hidden.")) return;
    try {
      await deleteModule(courseId, selectedSubject._id, moduleId);
      if (selectedModule?._id === moduleId) {
        setSelectedModule(null);
        setTopics([]);
      }
      await loadModules(selectedSubject._id);
      showToast("Module deleted");
    } catch { showToast("Error deleting module"); }
  };

  // Topic CRUD
  const handleAddTopic = async (title) => {
    if (!title.trim() || !selectedModule) return;
    try {
      await createTopic(courseId, selectedSubject._id, selectedModule._id, title.trim());
      setAddingTopic(false);
      await loadTopics(selectedSubject._id, selectedModule._id);
      showToast("Topic added");
    } catch { showToast("Error adding topic"); }
  };

  const handleUpdateTopic = async (topicId, title) => {
    if (!title.trim()) return;
    try {
      await updateTopic(courseId, selectedSubject._id, selectedModule._id, topicId, title.trim());
      setEditingTopicId(null);
      await loadTopics(selectedSubject._id, selectedModule._id);
      showToast("Topic updated");
    } catch { showToast("Error updating topic"); }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm("Delete this topic?")) return;
    try {
      await deleteTopic(courseId, selectedSubject._id, selectedModule._id, topicId);
      await loadTopics(selectedSubject._id, selectedModule._id);
      showToast("Topic deleted");
    } catch { showToast("Error deleting topic"); }
  };

  return (
    <div className="cn-page">
      {toast && <div className="cn-toast">{toast}</div>}

      <div className="cn-header">
        <div>
          <h2 className="cn-title">Notes Management</h2>
          {course && <p className="cn-subtitle">{course.courseTitle}</p>}
        </div>
        <button className="cn-back-btn" onClick={() => navigate(`/courses/${courseId}`)}>
          ← Back to Course
        </button>
      </div>

      <div className="cn-columns">
        {/* ── Column 1: Subjects ── */}
        <div className="cn-col">
          <div className="cn-col-header">
            <span className="cn-col-title">Subjects</span>
            <button
              className="cn-add-btn"
              style={{ fontSize: 11 }}
              onClick={() => navigate(`/courses/${courseId}/update`)}
            >
              Manage →
            </button>
          </div>

          {loadingSubjects && <div className="cn-loading">Loading...</div>}

          {!loadingSubjects && subjects.length === 0 && (
            <p className="cn-empty">No subjects linked. Add subjects from the course edit screen.</p>
          )}

          {subjects.map(subj => (
            <div
              key={subj._id}
              className={`cn-item${selectedSubject?._id === subj._id ? " cn-item-active" : ""}`}
              onClick={() => handleSelectSubject(subj)}
            >
              {editingSubjectId === subj._id ? (
                <InlineEditForm
                  initial={subj.title}
                  onSave={(v) => handleUpdateSubject(subj._id, v)}
                  onCancel={() => setEditingSubjectId(null)}
                />
              ) : (
                <>
                  <span className="cn-item-title">{subj.title}</span>
                  <div className="cn-item-btns">
                    <button
                      className="cn-edit-btn"
                      onClick={e => { e.stopPropagation(); setEditingSubjectId(subj._id); }}
                    >
                      Edit
                    </button>
                    <button className="cn-del-btn" onClick={e => handleDeleteSubject(e, subj._id)}>
                      Del
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* ── Column 2: Modules ── */}
        <div className="cn-col">
          <div className="cn-col-header">
            <span className="cn-col-title">Modules</span>
            {selectedSubject && (
              <button className="cn-add-btn" onClick={() => setAddingModule(true)}>+ Add</button>
            )}
          </div>

          {!selectedSubject && <p className="cn-empty cn-empty-hint">← Select a subject</p>}

          {selectedSubject && addingModule && (
            <InlineForm
              placeholder="Module title..."
              onSave={handleAddModule}
              onCancel={() => setAddingModule(false)}
            />
          )}

          {selectedSubject && loadingModules && <div className="cn-loading">Loading...</div>}

          {selectedSubject && !loadingModules && modules.length === 0 && !addingModule && (
            <p className="cn-empty">No modules yet.</p>
          )}

          {modules.map(mod => (
            <div
              key={mod._id}
              className={`cn-item${selectedModule?._id === mod._id ? " cn-item-active" : ""}`}
              onClick={() => handleSelectModule(mod)}
            >
              {editingModuleId === mod._id ? (
                <InlineEditForm
                  initial={mod.title}
                  onSave={(v) => handleUpdateModule(mod._id, v)}
                  onCancel={() => setEditingModuleId(null)}
                />
              ) : (
                <>
                  <span className="cn-item-title">{mod.title}</span>
                  <div className="cn-item-btns">
                    <button
                      className="cn-edit-btn"
                      onClick={e => { e.stopPropagation(); setEditingModuleId(mod._id); }}
                    >
                      Edit
                    </button>
                    <button className="cn-del-btn" onClick={e => handleDeleteModule(e, mod._id)}>
                      Del
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* ── Column 3: Topics ── */}
        <div className="cn-col">
          <div className="cn-col-header">
            <span className="cn-col-title">Topics</span>
            {selectedModule && (
              <button className="cn-add-btn" onClick={() => setAddingTopic(true)}>+ Add</button>
            )}
          </div>

          {!selectedModule && <p className="cn-empty cn-empty-hint">← Select a module</p>}

          {selectedModule && addingTopic && (
            <InlineForm
              placeholder="Topic title..."
              onSave={handleAddTopic}
              onCancel={() => setAddingTopic(false)}
            />
          )}

          {selectedModule && loadingTopics && <div className="cn-loading">Loading...</div>}

          {selectedModule && !loadingTopics && topics.length === 0 && !addingTopic && (
            <p className="cn-empty">No topics yet.</p>
          )}

          {topics.map(topic => (
            <div key={topic._id} className="cn-item cn-topic-item">
              {editingTopicId === topic._id ? (
                <InlineEditForm
                  initial={topic.title}
                  onSave={(v) => handleUpdateTopic(topic._id, v)}
                  onCancel={() => setEditingTopicId(null)}
                />
              ) : (
                <>
                  <span className="cn-item-title">{topic.title}</span>
                  <div className="cn-item-btns">
                    <button
                      className="cn-view-btn"
                      onClick={() => navigate(`/courses/${courseId}/topics/${topic._id}`)}
                    >
                      View →
                    </button>
                    <button
                      className="cn-edit-btn"
                      onClick={() => setEditingTopicId(topic._id)}
                    >
                      Edit
                    </button>
                    <button className="cn-del-btn" onClick={() => handleDeleteTopic(topic._id)}>
                      Del
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CourseNotesPage;
